import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchAllTags } from "store/actions/tagsList";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { addTag, deleteTagApi, fetchTagsApi } from "api/tagsList";

import getLogger from "utils/logger.util";
import { getRowFormattedData } from "utils/utils";
import { dialogStrings, TAGLIST_TABLE_COLUMNS, TagPageStrings } from "constants/constantStrings";

import AddTagAlarmModal from "components/AddTagAlarmModal";
import AgTable from "components/AgTable";
import BadgeLabel from "components/BadgeLabel";
import DeleteTagAlarmModal from "components/DeleteTagAlarmModal/DeleteTagAlarmModal";
import DupontButton from "components/DupontButton";
import Filter from "components/Filter";
import { OverlayLoader } from "components/PageLoading";
import TableDialog from "components/TableDialog";

import { getTagListTableColDef } from "./TagList.config";
import TagListEditForm from "./TagListEditForm.jsx";

import "./index.scss";

const TagList = () => {
  const { user, tags } = useSelector(state => state);
  const { tagsAsObj } = tags;
  const { isAdmin } = user;
  const dispatch = useDispatch();
  const Logger = getLogger("TagList");

  const [fetching, setFetching] = useState(true);
  const [filterValue, setFilterValue] = useState("");
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [popupData, setPopupData] = useState();
  const [tagsData, setTagsData] = useState([]);
  const [tagListBadgeCount, setTagListBadgeCount] = useState(0);
  const [showAddTagDialog, setShowAddTagDialog] = useState(false);

  const TABLE_COL_DEF = getTagListTableColDef(showTableFilterIcon, user.isAdmin);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    if (tagsAsObj) {
      const tagList = Object.values(tagsAsObj) || [];
      const tagsCount = tagList.length;
      setFetching(tagsCount === 0);
      setTagListBadgeCount(tagsCount);
      setTagsData(tagList);
    }
  }, [tagsAsObj]);

  const handleEditClick = rowData => {
    objAppInsights.trackEvent("editTag");
    const { TagId, TagName, Description, Units } = rowData;
    setSelectedRow({ TagId, TagName, Description, Units });
  };

  const handleDeleteClick = rowData => {
    objAppInsights.trackEvent("deleteTag");
    const { TagId } = rowData;
    setDeleteRow({ TagId });
  };
  const editTagFormCloseHandler = () => setSelectedRow(null);
  const onDeleteDlgClose = async action => {
    setDeleteRow(null);
    if (action === dialogStrings.confirm) {
      await deleteTag();
    }
  };
  const handleAddTagClose = () => setShowAddTagDialog(false);
  const handleAddClick = () => {
    setShowAddTagDialog(true);
  };
  const handleAddTagSubmit = async formData => {
    try {
      await addTag(formData);
      setFetching(true);
      const response = await fetchTagsApi();
      if (response && response.data && response.data.Tags) {
        dispatch(fetchAllTags(response?.data?.Tags));
        setShowAddTagDialog(false);
      }
    } catch (error) {
      Logger.log("Error adding tag:", error);
    } finally {
      setFetching(false);
    }
  };

  const deleteTag = async () => {
    try {
      setFilterValue("");
      setFetching(true);
      await deleteTagApi(deleteRow.TagId);
      const response = await fetchTagsApi();
      if (response && response.data && response.data.Tags) {
        dispatch(fetchAllTags(response?.data?.Tags));
      }
    } catch (error) {
      Logger.log("Error deleting tag:", error);
    } finally {
      setFetching(false);
    }
  };

  const closePopup = () => setPopupData(null);
  const onRowClicked = ({ data }) => {
    objAppInsights.trackEvent("tagsRowClick");
    const rowData = getRowFormattedData(TABLE_COL_DEF, data);
    const title = data.TagId;
    const subTitle = TAGLIST_TABLE_COLUMNS.viewingTag;
    setPopupData({ title, subTitle, rowData });
  };

  return (
    <>
      {fetching ? (
        <OverlayLoader />
      ) : (
        <div className={`app-wrapper TagList`}>
          <div className="tags-header">
            <BadgeLabel badgeDescription={TagPageStrings.taglist} badgeText={tagListBadgeCount} />
            {isAdmin && <DupontButton onClick={handleAddClick}>{TagPageStrings.addTag}</DupontButton>}
          </div>
          <div className="flex-between-center">
            <div className="descriptionText marginBtm5">{TagPageStrings.updateTag}</div>
            <div className="autoWidth">
              <Filter
                data={tagsData}
                setFilterValue={setFilterValue}
                showfilter={false}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
              />
            </div>
          </div>
          <div>
            <AgTable
              rowData={tagsData}
              onRowClicked={onRowClicked}
              quickFilterText={filterValue}
              columnDefs={TABLE_COL_DEF}
              context={{ handleEditClick, handleDeleteClick }}
              showfilter={false}
              setBadgeCount={setTagListBadgeCount}
              suppressScrollOnNewData
            />
            {selectedRow && <TagListEditForm selectedTagDetails={selectedRow} editTagFormCloseHandler={editTagFormCloseHandler} />}
            {showAddTagDialog && <AddTagAlarmModal handleClose={handleAddTagClose} Tags={tagsData} onFormSubmit={handleAddTagSubmit} />}
            {popupData && <TableDialog popupData={popupData} closePopup={closePopup} />}
            {deleteRow && <DeleteTagAlarmModal deleteRow={deleteRow} onDeleteDlgClose={onDeleteDlgClose} />}
          </div>
        </div>
      )}
    </>
  );
};

export default TagList;
